/**
 * External (non-theme) add-to-cart → live cart-drawer refresh.
 *
 * UB-QA-102 (Figma QA, 2026-07-20): "On an empty cart, when I click 'Quick add' nothing happens,
 * the product disappears but my cart doesn't update live with the added product. I have to close
 * the minicart, refresh the page, then re-open to see the added items."
 *
 * Root cause: the cart drawer's "It's a match" upsell carousel is rendered by the Rebuy app
 * (snippets/rebuy-widget-cart.liquid, `.cart-recommendations__quick-add` / `.rebuy-button`) — its
 * "Quick add" button posts to Shopify's `/cart/add.js` through RENBUY'S OWN script, entirely
 * outside our `<product-form>` / `assets/cart-drawer.js` flow, so `CartDrawer.renderContents()`
 * never runs and the drawer keeps showing its stale (often "empty") pre-add state. Confirmed live:
 * `/cart.js` shows `item_count` incremented immediately after the click, but the drawer DOM does
 * not change until a manual page refresh.
 *
 * Fix: since we don't own Rebuy's click handler, the only reliable hook is the network layer —
 * wrap `window.fetch` ONCE (idempotent) and watch for a successful POST to `/cart/add(.js)`
 * regardless of who made the call. When one resolves, pull fresh `cart-drawer` +
 * `cart-icon-bubble` section HTML via Shopify's Section Rendering API (`?sections=...` works on
 * ANY storefront URL, not just the add endpoint) and feed it into the SAME `renderContents()` our
 * own theme's product-form flow already uses — so the drawer updates identically no matter which
 * script triggered the add. Our own ATC flow calling this too is a harmless, idempotent no-op (it
 * just re-renders the same fresh data a second time).
 */

const ADD_TO_CART_PATH_RE = /\/cart\/add(?:\.js)?(?:\?|$)/;

// UBB-364: Loop bundle pages (/a/loop_subscriptions/bundle/*) handle their own post-add
// redirect. Syncing the cart drawer here caused the reported flash-then-disappear: our fetch
// wrapper triggers renderContents() → open() before Loop's redirect fires. Guarding by
// pathname keeps the fix narrowly scoped — all other pages behave as before.
const LOOP_BUNDLE_PATH_RE = /\/a\/loop_subscriptions\/bundle/;

function refreshCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;

  // Fetch sections HTML + cart JSON in parallel — sections to refresh the drawer,
  // cart to give the Loop re-init handler fresh item data via cart:drawer-refreshed.
  Promise.all([
    fetch(
      `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`
    ).then((r) => r.json()),
    fetch('/cart.json').then((r) => r.json()),
  ])
    .then(([sections, cart]) => {
      cartDrawer.renderContents({ id: null, sections });
      // Clear the in-flight flag before dispatching so cart:drawer-refreshed listeners
      // (e.g. Loop re-init) run cleanly without seeing a stale flag.
      window.__cartAddSyncInFlight = false;
      // Dispatch after renderContents() so the drawer DOM is already updated when
      // listeners (e.g. Loop re-init) run.
      document.dispatchEvent(
        new CustomEvent('cart:drawer-refreshed', { detail: { cart } })
      );
    })
    .catch(() => {
      window.__cartAddSyncInFlight = false; // ensure cleanup on error
      /* best-effort — a failed refresh just leaves the drawer as-is, no worse than before this fix */
    });
}

function initExternalCartAddSync() {
  if (window.__externalCartAddSyncInit) return; // idempotent — bind the fetch wrapper once
  window.__externalCartAddSyncInit = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const method = (
      (init && init.method) ||
      (input && input.method) ||
      'GET'
    ).toUpperCase();
    const isCartAdd = url && method === 'POST' && ADD_TO_CART_PATH_RE.test(url);

    const requestPromise = originalFetch(input, init);
    if (!isCartAdd) return requestPromise;

    return requestPromise.then((response) => {
      if (response.ok) {
        // UBB-364: Skip cart-drawer sync entirely on the Loop bundle page. Loop redirects
        // away immediately after the add; opening the drawer here caused the reported
        // flash-then-disappear. No in-flight flag needed either — priority-processing-reload.js
        // listens to cart:drawer-refreshed which we won't dispatch, and Loop's own redirect
        // takes the user off the page before any drawer state matters.
        //
        // Previous behaviour (unconditional — now guarded; kept for reference):
        // window.__cartAddSyncInFlight = true;
        // refreshCartDrawer();
        if (LOOP_BUNDLE_PATH_RE.test(window.location.pathname)) return response;

        // Signal that an add-to-cart is in flight so the cart:change handler in
        // priority-processing-reload.js suppresses its reinit. This .then() runs
        // before Motto's handler (they await our returned promise), so the flag is
        // already set when cart:change fires. cart:drawer-refreshed clears it after
        // the full drawer refresh completes.
        window.__cartAddSyncInFlight = true;
        refreshCartDrawer();
      }
      return response;
    });
  };
}

initExternalCartAddSync();

export default initExternalCartAddSync;
